import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
    Menu,
    X,
    LogIn,
    LogOut,
    ArrowRight,
    Sparkles,
    LayoutDashboard,
} from "lucide-react";
import BrandLogo from "./BrandLogo";
import { NAV_LINKS } from "../lib/constants";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { useAuth } from "@/lib/auth";

const STAFF_ROLES = ["master_admin", "admin"];

export default function Header() {
    const [open, setOpen] = useState(false);
    const { user, signOut } = useAuth();
    const { pathname } = useLocation();

    const isStaff = !!user && STAFF_ROLES.includes(user.role);
    const emailShort = user ? user.email.split("@")[0] : "";

    return (
        <header
            className="sticky top-0 z-40 border-b border-mm-line bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70"
            data-testid="site-header"
        >
            <div className="mm-container flex h-14 items-center gap-3 sm:h-20 sm:gap-6">
                <BrandLogo size={30} />

                <nav
                    className="ml-4 hidden xl:flex items-center gap-6 text-[0.9rem] text-mm-navy/90"
                    aria-label="Primary"
                    data-testid="primary-nav"
                >
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            className={({ isActive }) =>
                                `relative py-1 transition-colors hover:text-mm-blue ${
                                    isActive || (l.to !== "/" && pathname.startsWith(l.to))
                                        ? "text-mm-blue"
                                        : ""
                                }`
                            }
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="ml-auto flex items-center gap-2 sm:gap-3">
                    {user ? (
                        <div className="hidden md:flex items-center gap-2" data-testid="header-user-chip">
                            <span className="mm-chip" title={user.email}>
                                <Sparkles size={12} className="text-mm-blue" />
                                {emailShort}
                            </span>
                            {isStaff && (
                                <Link
                                    to="/admin"
                                    className="mm-btn-primary !px-4 !py-2 text-[0.82rem]"
                                    data-testid="header-dashboard-cta"
                                >
                                    <LayoutDashboard size={14} aria-hidden />
                                    Dashboard
                                </Link>
                            )}
                            <Link
                                to="/account/password"
                                className="text-sm font-medium text-mm-navy/80 hover:text-mm-blue hover:underline underline-offset-4"
                                data-testid="header-change-password"
                                title="Change password"
                            >
                                Account
                            </Link>
                            <button
                                onClick={signOut}
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-mm-navy/80 hover:text-mm-blue"
                                data-testid="header-signout"
                            >
                                <LogOut size={16} aria-hidden />
                                Sign out
                            </button>
                        </div>
                    ) : (
                        <Link
                            to={`/login?next=${encodeURIComponent(pathname)}`}
                            className="hidden md:inline-flex items-center gap-1.5 text-sm font-medium text-mm-navy/90 transition-colors hover:text-mm-blue hover:underline underline-offset-4"
                            data-testid="header-signin-button"
                        >
                            <LogIn size={14} aria-hidden />
                            Sign In
                        </Link>
                    )}
                    <Link
                        to="/#lead-form"
                        className="mm-btn-primary !px-4 !py-2.5 text-[0.82rem] sm:!px-5"
                        data-testid="header-cta-quote"
                    >
                        <span className="sm:hidden">Get Quote</span>
                        <span className="hidden sm:inline">Get My Free Quote</span>
                        <ArrowRight size={16} aria-hidden />
                    </Link>
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                className="inline-flex xl:hidden h-11 w-11 items-center justify-center rounded-full border border-mm-line bg-white text-mm-navy transition-colors hover:bg-mm-bg"
                                aria-label="Open menu"
                                data-testid="mobile-menu-toggle"
                            >
                                <Menu size={20} />
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            side="right"
                            className="w-[88vw] sm:w-[380px] bg-white p-0"
                            data-testid="mobile-menu-drawer"
                        >
                            <SheetTitle className="sr-only">Navigation</SheetTitle>
                            <div className="flex items-center justify-between border-b border-mm-line px-5 py-4">
                                <BrandLogo size={26} />
                                <button
                                    onClick={() => setOpen(false)}
                                    className="inline-flex h-11 w-11 items-center justify-center rounded-full text-mm-navy hover:bg-mm-bg"
                                    aria-label="Close menu"
                                    data-testid="mobile-menu-close"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                            <nav
                                className="flex flex-col px-4 py-4"
                                aria-label="Mobile"
                                data-testid="mobile-nav"
                            >
                                {/* Sign-in (or signed-in user actions) — TOP of menu, not buried */}
                                {user ? (
                                    <div className="mb-3 flex flex-col gap-2 rounded-xl bg-mm-bg/60 px-3 py-3">
                                        <div className="flex items-center gap-2 text-xs text-mm-muted">
                                            <Sparkles size={12} className="text-mm-blue" />
                                            <span className="font-mono">{user.email}</span>
                                        </div>
                                        {isStaff && (
                                            <Link
                                                to="/admin"
                                                onClick={() => setOpen(false)}
                                                className="mm-btn-primary w-full !py-3"
                                                data-testid="mobile-dashboard-cta"
                                            >
                                                <LayoutDashboard size={14} />
                                                Open Dashboard
                                            </Link>
                                        )}
                                        <button
                                            onClick={() => {
                                                signOut();
                                                setOpen(false);
                                            }}
                                            className="mm-btn-outline w-full !py-2.5"
                                            data-testid="mobile-signout"
                                        >
                                            <LogOut size={14} />
                                            Sign out
                                        </button>
                                    </div>
                                ) : (
                                    <Link
                                        to={`/login?next=${encodeURIComponent(pathname)}`}
                                        onClick={() => setOpen(false)}
                                        className="mb-3 inline-flex items-center gap-2 rounded-lg px-3 py-3 text-[0.95rem] font-medium text-mm-navy hover:bg-mm-bg"
                                        data-testid="mobile-signin-link"
                                    >
                                        <LogIn size={14} aria-hidden />
                                        Sign In
                                    </Link>
                                )}

                                {NAV_LINKS.map((l) => (
                                    <NavLink
                                        key={l.to}
                                        to={l.to}
                                        onClick={() => setOpen(false)}
                                        className={({ isActive }) =>
                                            `rounded-lg px-3 py-3 text-[0.95rem] transition-colors ${
                                                isActive
                                                    ? "bg-mm-bg text-mm-blue"
                                                    : "text-mm-navy hover:bg-mm-bg"
                                            }`
                                        }
                                        data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    >
                                        {l.label}
                                    </NavLink>
                                ))}
                                <div className="mt-3 flex flex-col gap-2 border-t border-mm-line px-1 pt-4">
                                    <Link
                                        to="/#lead-form"
                                        onClick={() => setOpen(false)}
                                        className="mm-btn-primary w-full !py-3"
                                        data-testid="mobile-cta-quote"
                                    >
                                        Get My Free Quote
                                    </Link>
                                </div>
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
